import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';

import { deleteClientLocation } from '@/api/client';
import FormItem from '@/components/form/FormItem';
import {
  useFormSchema,
  useValidationScope,
} from '@/components/form/FormSchemaContext';
import DynamicFieldList from '@/components/form/shared/DynamicFieldList';
import GooglePlacesInput from '@/components/form/shared/GooglePlacesInput';
import { useApiFeedback } from '@/hooks/useApiFeedback';

import type { SizeType } from 'antd/es/config-provider/SizeContext';

type OfficeLocation = {
  id: string;
  address: string;
};

type OfficeLocationsFieldProps = {
  name?: string | (string | number)[];
  label?: string;
  tooltip?: string;
  help?: string;
  companyId?: string;
  size?: SizeType;
  lockExisting?: boolean;
};

type FormAddressInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  size?: SizeType;
  disabled?: boolean;
};

const FormAddressInput = ({
  value,
  onChange,
  size,
  disabled,
}: FormAddressInputProps) => (
  <GooglePlacesInput
    mode="address"
    value={value}
    onChange={onChange}
    size={size}
    disabled={disabled}
  />
);

const OfficeLocationsField = ({
  name = 'office_locations',
  label,
  tooltip,
  help,
  companyId,
  size,
  lockExisting = false,
}: OfficeLocationsFieldProps) => {
  const [t] = useTranslation();
  const { showSuccess } = useApiFeedback();
  const form = Form.useFormInstance();
  const formSchema = useFormSchema();
  const validationScope = useValidationScope();
  const fieldLabel = label ?? t('OFFICE_LOCATIONS_LABEL');
  const required =
    formSchema?.isFieldRequired(
      'office_locations',
      undefined,
      validationScope,
    ) ?? false;

  const handleRemove = (index: number) => {
    const locations =
      (form.getFieldValue(name) as OfficeLocation[] | undefined) ?? [];
    const location = locations[index];
    if (lockExisting && location?.id) {
      return;
    }
    if (location?.id && companyId) {
      deleteClientLocation(location.id, companyId)
        .then(() => showSuccess('LOCATION_DELETE_SUCCESS'))
        .catch(() => undefined);
    }
  };

  const isExistingLocationLocked = (index: number) => {
    if (!lockExisting) {
      return false;
    }
    const locations =
      (form.getFieldValue(name) as OfficeLocation[] | undefined) ?? [];
    return Boolean(locations[index]?.id);
  };

  return (
    <div id="office-locations-field">
      <FormItem
        label={fieldLabel}
        tooltip={tooltip}
        help={help}
        required={required}
      >
        <DynamicFieldList
          name={name}
          min={0}
          defaultItem={{ id: '', address: '' }}
          addLabel={t('ADD_LOCATION_TITLE')}
          onRemove={handleRemove}
          isRowRemovable={(index) => !isExistingLocationLocked(index)}
          inputSize={size}
          renderRow={({ field, index }) => [
            {
              raw: true,
              content: (
                <Form.Item name={[field.name, 'id']} hidden>
                  <Input />
                </Form.Item>
              ),
            },
            {
              name: 'address',
              input: (
                <FormAddressInput
                  size={size}
                  disabled={isExistingLocationLocked(index)}
                />
              ),
            },
          ]}
        />
      </FormItem>
    </div>
  );
};

export default OfficeLocationsField;
